// StreakHeatmap — 30-day grid. Desktop grid-cols-10, mobile grid-cols-7.

import { cn } from "@studyhub/ui/lib/cn";
import { IconSnowflake } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { StreakDay } from "./_api";

interface Props {
	history: StreakDay[];
	cols?: number;
}

function statusClass(status: StreakDay["status"]): string {
	if (status === "goal-met") return "bg-success/40";
	if (status === "active") return "bg-success/20";
	if (status === "frozen") return "bg-info/30 ring-1 ring-info/40";
	if (status === "today") return "bg-success/30 ring-2 ring-primary ring-inset";
	return "bg-muted";
}

export function StreakHeatmap({ history, cols = 10 }: Props): React.ReactElement {
	const { t, i18n } = useTranslation("streaks");
	return (
		<div className="space-y-2">
			<div className={cn("grid gap-1", cols === 7 ? "grid-cols-7" : "grid-cols-10")}>
				{history.map((day) => {
					const dateLabel = new Date(day.date).toLocaleDateString(i18n.language, {
						day: "2-digit",
						month: "short",
					});
					const detail =
						day.status === "frozen"
							? t("heatmap.frozenCell")
							: t("heatmap.cellMinutes", { count: day.studyMinutes });
					const title = `${dateLabel} · ${detail}`;
					return (
						<div
							key={day.date}
							role="img"
							title={title}
							aria-label={title}
							className={cn(
								"aspect-square rounded-sm relative flex items-center justify-center transition-transform hover:scale-110",
								statusClass(day.status),
							)}
						>
							{day.status === "frozen" && (
								<IconSnowflake size={10} className="text-info" stroke={2} />
							)}
						</div>
					);
				})}
			</div>
			<div className="flex items-center gap-3 text-2xs text-muted-foreground">
				<span className="inline-flex items-center gap-1">
					<span className="size-3 rounded-sm bg-success/40" />
					{t("heatmap.legend.done")}
				</span>
				<span className="inline-flex items-center gap-1">
					<span className="size-3 rounded-sm bg-info/30 ring-1 ring-info/40" />
					{t("heatmap.legend.frozen")}
				</span>
				<span className="inline-flex items-center gap-1">
					<span className="size-3 rounded-sm bg-muted" />
					{t("heatmap.legend.missed")}
				</span>
			</div>
		</div>
	);
}
